/**
 * آیکون‌های خطیِ سایت — قرینه‌ی مفهومیِ apps/mobile/src/ui/icons.tsx، ولی
 * پیاده‌سازیِ جدا: آن یکی روی react-native-svg است که روی وب اجرا نمی‌شود.
 * اینجا SVGِ خامِ HTML است — بدون کتابخانه‌ی جدید، فقط چند مسیرِ ساده.
 *
 * سبک: خطِ نازک (stroke) در حالتِ عادی؛ در حالتِ «فعال» به‌جای اضافه کردنِ
 * رنگ، خودِ شکل توپر می‌شود — دقیقاً همان چیزی که برای دسته‌های کشف خواسته
 * شده بود.
 */
import React from 'react';
import { categoryShape } from '@/core/lib/categoryShapes';

interface IconProps { size?: number; className?: string; }

/** سطلِ زباله — جایگزینِ متنِ «حذف»/«remove» در فهرست‌ها. */
export function TrashIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 6h12" />
      <path d="M8 6V4.5a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1V6" />
      <path d="M5.5 6l.6 9.5a1.5 1.5 0 0 0 1.5 1.4h4.8a1.5 1.5 0 0 0 1.5-1.4L14.5 6" />
      <path d="M8.3 9v5M11.7 9v5" />
    </svg>
  );
}

/** مداد — «ویرایش» روی ردیفِ دوستی که هنوز عضو نیست (WHOSUP-144). */
export function PencilIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14.2 3.1a2 2 0 0 1 2.8 2.8L7 16l-3.9 1L4 13.1 14.2 3.1Z" />
      <path d="M12.5 4.8l2.8 2.8" />
    </svg>
  );
}

/** چشم / چشمِ خط‌خورده — نشان دادن و پنهان کردنِ رمز (WHOSUP-363). همان شکلِ موبایل. */
export function EyeIcon({ size = 18, className, off }: IconProps & { off?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      {off ? (
        <>
          <path d="M9.6 5.9A9.9 9.9 0 0 1 12 5.6c5.9 0 9.5 6.4 9.5 6.4a17 17 0 0 1-2.8 3.6" />
          <path d="M6.3 7.7A16.6 16.6 0 0 0 2.5 12s3.6 6.2 9.5 6.2c1.6 0 3-.3 4.2-.9" />
          <path d="M4 4l16 16" />
        </>
      ) : (
        <>
          <path d="M2.5 12s3.6-6.2 9.5-6.2S21.5 12 21.5 12s-3.6 6.2-9.5 6.2S2.5 12 2.5 12z" />
          <circle cx="12" cy="12" r="2.9" />
        </>
      )}
    </svg>
  );
}

type CategorySlug = 'nature' | 'culture' | 'food' | 'sport' | 'game' | 'travel' | 'home' | 'other';

/** بدنه‌ی هر شکل — یک‌بار نوشته می‌شود، هم برای حالتِ خطی هم توپر استفاده می‌شود. */
const SHAPES: Record<CategorySlug, { outline: string; filled: string }> = {
  nature: {
    outline: 'M10 3 4 13h4l-2.5 4h9L12 13h4L10 3Z M10 17v2.5',
    filled: 'M10 2.3 3.3 13.6h4.2L4.7 18h10.6l-3-4.4h4.4L10 2.3Z M10 17v2.6',
  },
  culture: {
    outline: 'M4 5.5h12v9.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V5.5Z M4 5.5 10 2l6 3.5 M7.5 9.5a1.2 1.2 0 1 0 2.4 0 1.2 1.2 0 0 0-2.4 0Z M10.5 12.5l2-2.3 3 4.8H6l2.6-3.4 1.9 1Z',
    filled: 'M3.3 5 10 1.2 16.7 5 16 6.3 10 3 4 6.3 3.3 5Z M4 6h12v9.5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V6Z',
  },
  food: {
    outline: 'M6 2.5v6a1.5 1.5 0 0 0 1.5 1.5v7.5 M6 2.5v4 M8 2.5v4 M8 2.5v15 M14.5 2.5c-1.4 0-2.5 1.8-2.5 5s1.1 3.5 2.5 3.5V17.5',
    filled: 'M6 2.3v5.9c0 .9.6 1.6 1.5 1.8v7.5h1V2.3h-1v5.4h-.2V2.3h-1v5.4H7V2.3H6Z M14.9 2.3c-1.7 0-3 2.2-3 5.2 0 2.6 1 3.9 2.5 4.2v5.8h1V2.3h-.5Z',
  },
  sport: {
    outline: 'M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z M10 3.2v13.6 M3.2 10h13.6 M5 5l10 10 M15 5 5 15',
    filled: 'M10 17a7 7 0 1 0 0-14 7 7 0 0 0 0 14Z',
  },
  game: {
    outline: 'M5 3.5h10A1.5 1.5 0 0 1 16.5 5v10a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 15V5A1.5 1.5 0 0 1 5 3.5Z M7 7a.9.9 0 1 0 0-1.8A.9.9 0 0 0 7 7Z M13 7a.9.9 0 1 0 0-1.8A.9.9 0 0 0 13 7Z M10 10.9a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z M7 14.8a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z M13 14.8a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z',
    filled: 'M5 3.5h10A1.5 1.5 0 0 1 16.5 5v10a1.5 1.5 0 0 1-1.5 1.5H5A1.5 1.5 0 0 1 3.5 15V5A1.5 1.5 0 0 1 5 3.5Z',
  },
  travel: {
    outline: 'M7 6.5V5a1.5 1.5 0 0 1 1.5-1.5h3A1.5 1.5 0 0 1 13 5v1.5 M4.5 8A1.5 1.5 0 0 1 6 6.5h8A1.5 1.5 0 0 1 15.5 8v7A1.5 1.5 0 0 1 14 16.5H6A1.5 1.5 0 0 1 4.5 15V8Z M4.5 11.5h11',
    filled: 'M7 6.2V5a1.6 1.6 0 0 1 1.6-1.6h2.8A1.6 1.6 0 0 1 13 5v1.2h-1V5a.6.6 0 0 0-.6-.6H8.6A.6.6 0 0 0 8 5v1.2H7Z M4.5 8A1.5 1.5 0 0 1 6 6.5h8A1.5 1.5 0 0 1 15.5 8v7A1.5 1.5 0 0 1 14 16.5H6A1.5 1.5 0 0 1 4.5 15V8Z',
  },
  home: {
    outline: 'M4 9.5 10 4l6 5.5 M5.5 8.5V16a.7.7 0 0 0 .7.7h3V13a.8.8 0 0 1 .8-.8h0a.8.8 0 0 1 .8.8v3.7h3a.7.7 0 0 0 .7-.7V8.5',
    filled: 'M10 2.9 2.7 9.6l1 1 1.3-1.2V16a1.2 1.2 0 0 0 1.2 1.2h2.9V13a.9.9 0 0 1 .9-.9h0a.9.9 0 0 1 .9.9v4.2h2.9A1.2 1.2 0 0 0 15 16V9.4l1.3 1.2 1-1L10 2.9Z',
  },
  other: {
    outline: 'M10.5 3.5H16v5.5l-8 8-5.5-5.5 8-8Z M12.8 6.2a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z',
    filled: 'M10.5 3.5H16v5.5l-8 8-5.5-5.5 8-8Z',
  },
};

export function CategoryIcon({ slug, active, size = 22, className }: {
  slug: string; active?: boolean; size?: number; className?: string;
}) {
  /**
   * تگ‌های ژانر (music-rock، arts-theatre، …) شکلشون از `packages/core`
   * می‌آد، چون گوشی هم عیناً همون‌ها رو می‌کشه — نگاه کن به
   * `core/lib/categoryShapes`. اون‌ها روی شبکه‌ی ۲۴ کشیده شدن، پس هم viewBox
   * و هم ضخامتِ قلم به همون نسبت (۲۴/۲۰) بزرگ‌تره تا کنارِ هشت دسته‌ی قدیمی
   * هم‌وزن به‌نظر برسه.
   */
  const genre = SHAPES[slug as CategorySlug] ? null : categoryShape(slug);
  if (genre) {
    return (
      <svg width={size} height={size} viewBox="0 0 24 24" className={className}
        fill={active ? 'currentColor' : 'none'}
        fillRule={genre.holes ? 'evenodd' : 'nonzero'}
        clipRule={genre.holes ? 'evenodd' : 'nonzero'}
        stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <path d={genre.body} />
        {genre.lines ? <path d={genre.lines} fill="none" /> : null}
      </svg>
    );
  }

  const shape = SHAPES[slug as CategorySlug] ?? SHAPES.other;
  return active ? (
    <svg width={size} height={size} viewBox="0 0 20 20" className={className} fill="currentColor">
      <path d={shape.filled} fillRule="evenodd" clipRule="evenodd" />
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 20 20" className={className} fill="none"
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d={shape.outline} />
    </svg>
  );
}

/**
 * سه نقطه — «دسته‌های بیشتر» در ردیفِ کشف.
 *
 * هم‌قدِ آیکون‌های دسته کشیده شده (شبکه‌ی ۲۰) تا در همان ردیف بنشیند و
 * ارتفاعِ کاشی‌ها را به هم نزند.
 */
export function MoreIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" className={className} fill="currentColor">
      <circle cx="4.5" cy="10" r="1.6" />
      <circle cx="10" cy="10" r="1.6" />
      <circle cx="15.5" cy="10" r="1.6" />
    </svg>
  );
}

/**
 * تقویم با یک «+» — شکلِ مشترکِ آیکونِ «Plans» و دکمه‌ی «رخداد تازه».
 *
 * یک‌جا نوشته شده تا دو کپیِ جدا از هم دور نیفتند؛ قرینه‌ی همین شکل روی
 * موبایل، نامِ `signal` در apps/mobile/src/ui/icons.tsx است.
 */
const CALENDAR_PLUS = {
  frame: 'M4 4.5h12a1 1 0 0 1 1 1V16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5.5a1 1 0 0 1 1-1Z',
  rings: 'M3 8.5h14M7 3v3M13 3v3',
  plus: 'M10 11v4M8 13h4',
};

/** آیکونِ «Plans» — برای بخشِ اولِ نوارِ کناری. */
export function SignalsIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      {/* تقویم + یه «+». تا وقتی اسمِ این بخش «سیگنال»/«Ideas» بود آذرخش و
          بعد لامپ می‌نشست؛ حالا که «Plans» است، هر دو خلافِ برچسبشون بودن. */}
      <path d={CALENDAR_PLUS.frame} />
      <path d={CALENDAR_PLUS.rings} />
      <path d={CALENDAR_PLUS.plus} />
    </svg>
  );
}

/** آیکونِ دوستان — دو نفر، برای بخشِ دومِ نوارِ کناری. */
export function FriendsIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M7.2 9.4a2.7 2.7 0 1 0 0-5.4 2.7 2.7 0 0 0 0 5.4Z" />
      <path d="M2.2 16c0-2.7 2.2-4.6 5-4.6s5 1.9 5 4.6" />
      <path d="M13.2 4.6a2.5 2.5 0 0 1 0 5" />
      <path d="M13.6 11.5c2.3.3 4.2 2.1 4.2 4.5" />
    </svg>
  );
}

/** آیکونِ فیلتر — برای دکمه‌ی پاپ‌آپِ فیلترهای کشف. */
export function FilterIcon({ size = 18, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 5h14M6 10h8M8.5 15h3" />
    </svg>
  );
}

/** آیکونِ ترتیب — برای دکمه‌ی پاپ‌آپِ Sort. */
export function SortIcon({ size = 18, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M6 4v12M6 16l-2.5-2.5M6 16l2.5-2.5" />
      <path d="M14 16V4M14 4l-2.5 2.5M14 4l2.5 2.5" />
    </svg>
  );
}

/**
 * قلبِ نشان‌کردن — قرینه‌ی آیکونِ `heart`ِ موبایل.
 *
 * همون قاعده‌ی بقیه‌ی آیکون‌های این فایل: نشان‌نشده خطیه، نشان‌شده توپر. یه
 * مسیر برای هر دو حالت، چون فرقشون فقط پر شدنِ داخلِ همون شکله.
 */
export function HeartIcon({ size = 18, filled, className }: IconProps & { filled?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" className={className}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 16.6 4.2 11a3.6 3.6 0 0 1 5.1-5.1l.7.7.7-.7A3.6 3.6 0 0 1 15.8 11L10 16.6Z" />
    </svg>
  );
}

/**
 * زنگوله‌ی دنبال کردنِ تگ (مایگریشن 0094) — قرینه‌ی آیکونِ `bell`ِ موبایل.
 * همون قاعده‌ی قلب: دنبال‌نشده خطی، دنبال‌شده توپر.
 */
export function BellIcon({ size = 18, filled, className }: IconProps & { filled?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" className={className}
      fill={filled ? 'currentColor' : 'none'}
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18 15.5V11a6 6 0 1 0-12 0v4.5L4.5 18h15z" />
      <path d="M10 20.5a2.2 2.2 0 0 0 4 0" />
    </svg>
  );
}

/** سنجاقِ نقشه — دکمه‌ی شهرِ کنارِ جستجو، و ردیفِ شهر توی تنظیمات. */
export function PinIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 17.5s5.5-4.7 5.5-9a5.5 5.5 0 1 0-11 0c0 4.3 5.5 9 5.5 9Z" />
      <circle cx="10" cy="8.4" r="2.1" />
    </svg>
  );
}

/**
 * پیکانِ باز/بسته — سربرگِ گروه‌های بازشونده در صفحه‌ی دوستان.
 *
 * عمداً رو به **پایین** کشیده شده و چرخشش توی CSS است، نه دو تا آیکونِ جدا:
 * جهتِ حالتِ بسته در راست‌به‌چپ برعکسِ چپ‌به‌راست است و اون تفاوت جای درستش
 * استایل‌شیته (نگاه کن به `.grp-caret` توی base.css).
 */
export function ChevronIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d="M5.5 7.5 10 12l4.5-4.5" />
    </svg>
  );
}

/**
 * تقویم و برچسبِ قیمت — دو ردیفِ «کِی» و «چند» در صفحه‌ی جزئیات (ردیفِ «کجا»
 * همان `PinIcon`ِ بالاست). قرینه‌ی `clock` و `tag`ِ آیکون‌های موبایل.
 */
export function CalendarIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 4.5h12a1 1 0 0 1 1 1V16a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5.5a1 1 0 0 1 1-1Z" />
      <path d="M3 8.5h14M7 3v3M13 3v3" />
    </svg>
  );
}

export function PriceIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.3 3H16a1 1 0 0 1 1 1v5.7a1 1 0 0 1-.3.7l-6.3 6.3a1 1 0 0 1-1.4 0l-5.7-5.7a1 1 0 0 1 0-1.4l6.3-6.3a1 1 0 0 1 .7-.3Z" />
      <path d="M13.2 7.3a.9.9 0 1 0 0-1.8.9.9 0 0 0 0 1.8Z" />
    </svg>
  );
}

/**
 * سه خط و ضربدر — دکمه‌ی منو در هدرِ گوشی (نگاه کن به app/MobileMenu.tsx).
 *
 * دو تا آیکونِ جدا و نه یکی که با CSS بچرخه: شکلِ بسته و باز هیچ خطِ مشترکی
 * ندارن، پس چرخاندن فقط پیچیدگی بود بی‌آنکه چیزی صرفه‌جویی کنه.
 */
export function MenuIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      <path d="M3 5.5h14M3 10h14M3 14.5h14" />
    </svg>
  );
}

export function CloseIcon({ size = 22, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
      <path d="M5 5l10 10M15 5 5 15" />
    </svg>
  );
}

/**
 * «لغو شد» — دایره‌ی خط‌خورده، قرینه‌ی آیکونِ `cancelled` در اپ.
 *
 * ✕ِ تنها استفاده نشد چون توی هر دو سطحِ محصول یعنی «ببند»؛ کنارِ عنوانِ یه
 * سیگنال شبیهِ دکمه‌ی بستنِ کارت می‌شد نه نشانِ وضعیت.
 */
export function CancelledIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
      <circle cx="10" cy="10" r="7" />
      <path d="M5.3 14.7 14.7 5.3" />
    </svg>
  );
}

/** آیکونِ رخداد/پیشنهاد — برای دکمه‌ی «رخداد تازه» کنارِ «برنامه‌ی جدید». */
export function EventPlusIcon({ size = 16, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
      <path d={CALENDAR_PLUS.frame} />
      <path d={CALENDAR_PLUS.rings} />
      <path d={CALENDAR_PLUS.plus} />
    </svg>
  );
}

/**
 * پرچم — دکمه‌ی «مشکلِ این رخداد» (features/discover/issues.tsx).
 *
 * همون قاعده‌ی قلب: تا مشکلی روی این رخداد ثبت نشده خطیه، و همین که یکی
 * ثبت شد توپر می‌شه. قرینه‌ی آیکونِ `flag`ِ موبایل.
 */
export function FlagIcon({ size = 16, filled, className }: IconProps & { filled?: boolean }) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" className={className}
      fill="none" stroke="currentColor" strokeWidth="1.6"
      strokeLinecap="round" strokeLinejoin="round">
      <path d="M5 17.5V3" />
      <path d="M5 4h9.5l-2 3.2 2 3.2H5" fill={filled ? 'currentColor' : 'none'} />
    </svg>
  );
}

/**
 * مثلثِ گوگل‌پلی — کنارِ «از گوگل‌پلی بگیرش» روی صفحه‌های بی‌حساب.
 *
 * عمداً تک‌رنگ و توپر است و نه نشانِ رسمیِ چهاررنگِ گوگل: نشانِ رسمی یک
 * فایلِ تصویری با قواعدِ برندِ خودش است و باید همان‌طور که هست استفاده شود.
 * تا وقتی اپ منتشر نشده و آن نشان را برنداشته‌ایم، این شکلِ ساده جای درستش
 * را روی صفحه نگه می‌دارد بی‌آنکه ادعای نشانِ رسمی داشته باشد.
 */
export function GooglePlayIcon({ size = 18, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="none" className={className}>
      <path d="M4.4 2.7a.9.9 0 0 1 1.4-.8l10 6.4a.8.8 0 0 1 0 1.4l-10 6.4a.9.9 0 0 1-1.4-.8V2.7Z"
        fill="currentColor" />
    </svg>
  );
}

/**
 * سیب — کنارِ «از اپ‌استور بگیرش»، قرینه‌ی `GooglePlayIcon` بالا.
 *
 * همان قاعده: نشانِ رسمیِ اپل یک فایلِ تصویری با قواعدِ برندِ خودش است و تا
 * وقتی اپ ثبت نشده برنداشته‌ایم؛ این شکلِ ساده جای درستش را نگه می‌دارد.
 */
export function AppleIcon({ size = 18, className }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor" className={className}>
      <path d="M13.9 10.6c0-1.7 1.4-2.5 1.5-2.6-.8-1.2-2-1.3-2.5-1.4-1-.1-2 .6-2.6.6-.5 0-1.4-.6-2.3-.6-1.2 0-2.3.7-2.9 1.8-1.2 2.1-.3 5.3.9 7 .6.9 1.3 1.8 2.2 1.8.9 0 1.2-.6 2.3-.6 1 0 1.3.6 2.3.5 1 0 1.6-.8 2.2-1.7.7-1 .9-1.9 1-2-.1 0-1.9-.7-1.9-2.8Z" />
      <path d="M12.2 5.5c.5-.6.8-1.4.7-2.2-.7 0-1.6.5-2.1 1.1-.5.5-.9 1.4-.7 2.2.8 0 1.6-.4 2.1-1.1Z" />
    </svg>
  );
}
